import EventBus from "../event-bus";
import validationMixin from "./validationMixin";
import { getDraftIdFromUrlCached } from "../utils/draftService";
import { safeGetJson } from "../utils/safeStorage";
import { incrementCustomizerMetric } from "../utils/sentry";

export default {
    mixins: [validationMixin],
    
    methods: {
        /**
         * Einfache Prüfung ob draft-id in URL vorhanden ist
         */
        hasDraftIdInUrl() {
            return !!getDraftIdFromUrlCached();
        },
        
        /**
         * Überprüft ob alle vorherigen Steps valide sind und navigiert falls nicht
         * Skip validation if draft-id is present (KISS fix for hash routing)
         */
        validateAndRedirectToPreviousSteps(pageSettings, currentStep) {
            if (!pageSettings) return false;

            if (process.env.VUE_APP_DEV_MODE === "true") {
                return false;
            }

            if (this.hasDraftIdInUrl()) {
                return false;
            }

            const stepOrder = ['StepOne', 'StepTwo', 'StepThree'];
            const currentIndex = stepOrder.indexOf(currentStep);

            if (currentIndex === -1) return false;

            for (let i = 0; i < currentIndex; i++) {
                const stepName = stepOrder[i];
                const storageKey = this.getStorageKeyForStep(pageSettings, stepName);
                
                if (!storageKey) continue;
                
                const stepValues = safeGetJson(storageKey, {});
                const hasEmptyRequiredFields = this.checkValues(stepValues);
                const hasNoValues = Object.keys(stepValues).length === 0;

                if (hasEmptyRequiredFields || hasNoValues) {
                    // Reached when a later step is opened directly (bookmark, reload). A sudden
                    // rise means the step guard is being bypassed, as with full-page step links.
                    incrementCustomizerMetric("customizer.redirected_to_previous_step", {
                        from: currentStep,
                        to: stepName,
                        reason: hasNoValues ? "no_values" : "required_missing",
                    });
                    const redirectUrl = this.getUrlForStep(pageSettings, stepName);
                    this.$router.push(redirectUrl);
                    return true;
                }
            }

            return false;
        },

        /**
         * Storage Key für Step zurückgeben
         */
        getStorageKeyForStep(pageSettings, stepName) {
            const keyMap = {
                'StepOne': pageSettings.step1StorageKey,
                'StepTwo': pageSettings.step2StorageKey,
                'StepThree': pageSettings.step3StorageKey
            };
            return keyMap[stepName] || '';
        },

        /**
         * URL für Step zurückgeben  
         */
        getUrlForStep(pageSettings, stepName) {
            const urlMap = {
                'StepOne': pageSettings.step1Url,
                'StepTwo': pageSettings.step2Url,
                'StepThree': pageSettings.step3Url
            };
            return urlMap[stepName] || '';
        },

        /**
         * Step validieren und MessageBox zeigen/verstecken
         */
        validateCurrentStepAndShowMessage(stepName, storageKey) {
            if (!storageKey || storageKey === "") {
                return false;
            }

            const stepValues = safeGetJson(storageKey, {});
            const shouldPreventNavigation = this.checkValues(stepValues);
            const messageBoxId = `${stepName.toLowerCase()}MessageBox`;

            if (shouldPreventNavigation) {
                EventBus.emit("showMessageBox", messageBoxId);
                EventBus.emit("throwErrorForRequiredInputs", true);
                
                this.scrollToFirstEmptyField();
                
                return true;
            }

            EventBus.emit("hideMessageBox", messageBoxId);
            EventBus.emit("throwErrorForRequiredInputs", false);
            return false;
        },

        /**
         * Navigation von StepTwo zu StepThree
         */
        handleCreateStoryNavigation() {
            const pageSettings = this.pageSettings || {};
            const step2StorageKey = pageSettings.step2StorageKey;
            
            if (!step2StorageKey) {
                this.$router.push(pageSettings.step3Url);
                return;
            }
            
            const isValidationFailed = this.validateCurrentStepAndShowMessage('stepTwo', step2StorageKey);
            if (!isValidationFailed) {
                this.$router.push(pageSettings.step3Url);
            }
        },

        /**
         * Universelle Navigation basierend auf dem aktuellen Step
         * @param {string} currentStep - Aktueller Step Name
         */
        handleStepNavigation(currentStep) {
            const pageSettings = this.pageSettings || {};
            
            if (this.isFinalStep(pageSettings, currentStep)) {
                if (this.orderNow) {
                    this.orderNow();
                }
            } else {
                const nextStepUrl = this.getNextStepUrl(pageSettings, currentStep);
                if (nextStepUrl) {
                    const storageKey = this.getStorageKeyForStep(pageSettings, currentStep);
                    const isValidationFailed = this.validateCurrentStepAndShowMessage(
                        currentStep.toLowerCase(), 
                        storageKey
                    );
                    
                    if (!isValidationFailed) {
                        this.$router.push(nextStepUrl);
                    }
                }
            }
        },

        /**
         * Ermittelt die URL für den nächsten Step
         * @param {Object} pageSettings - Page Settings
         * @param {string} currentStep - Aktueller Step Name
         * @returns {string|null} - URL für nächsten Step
         */
        getNextStepUrl(pageSettings, currentStep) {
            if (currentStep === "StepOne") {
                return pageSettings.step2Url;
            } else if (currentStep === "StepTwo") {
                return pageSettings.step3Url;
            }
            return null;
        },

        /**
         * Überprüft ob es sich um einen 2-Step Customizer handelt
         * @param {Object} pageSettings - Page Settings
         * @returns {boolean}
         */
        isTwoStepCustomizer(pageSettings) {
            return pageSettings.step3Url && 
                   (pageSettings.step3Url.includes('/checkout') || 
                    pageSettings.step3Url.includes('checkout'));
        },

        /**
         * Überprüft ob es sich um einen 1-Step Customizer handelt
         * @param {Object} pageSettings - Page Settings
         * @returns {boolean}
         */
        isOneStepCustomizer(pageSettings) {
            return pageSettings.step2Url && 
                   (pageSettings.step2Url.includes('/checkout') || 
                    pageSettings.step2Url.includes('checkout'));
        },

        /**
         * Überprüft ob es sich um einen "Skip Step 2" Customizer handelt (Step 1 -> Step 3)
         * @param {Object} pageSettings - Page Settings
         * @returns {boolean}
         */
        isSkipStep2Customizer(pageSettings) {
            return pageSettings.step2StorageKey === "" || 
                   pageSettings.step2StorageKey === null || 
                   pageSettings.step2StorageKey === undefined;
        },

        /**
         * Ermittelt ob der aktuelle Step der finale Step ist (sollte orderNow ausführen)
         * @param {Object} pageSettings - Page Settings
         * @param {string} currentStep - Aktueller Step Name
         * @returns {boolean}
         */
        isFinalStep(pageSettings, currentStep) {
            if (currentStep === "StepOne") {
                return this.isOneStepCustomizer(pageSettings);
            } else if (currentStep === "StepTwo") {
                return this.isTwoStepCustomizer(pageSettings);
            } else if (currentStep === "StepThree") {
                return true;
            }
            return false;
        },

        /**
         * Ermittelt die Anzahl der Steps basierend auf den URLs und Storage Keys
         * @param {Object} pageSettings - Page Settings
         * @returns {number} - Anzahl der Steps (1, 2, oder 3)
         */
        getCustomizerStepCount(pageSettings) {
            if (this.isOneStepCustomizer(pageSettings)) {
                return 1;
            } else if (this.isTwoStepCustomizer(pageSettings)) {
                return 2;
            } else if (this.isSkipStep2Customizer(pageSettings)) {
                return 2;
            } else {
                return 3;
            }
        }
    }
}; 
